// Filtrovanie zoznamu „Prebiehajúce výzvy" (2026-08).
//
// Logika žije mimo komponentu, lebo fasetové počty sú netriviálne: počet pri voľbe
// musí byť „koľko výziev ostane, keď nechám ostatné filtre tak, ako sú, a prepnem
// PRÁVE túto voľbu" — inak by pilulka ponúkala cestu do prázdneho zoznamu.
//
// Slovník (§6a bod 4): výzva · súťaž · náročnosť.

import { normDifficulty, type Difficulty } from "./points";

/** Položka z `GET /api/challenges/public` (len polia, na ktorých filter stojí). */
export type ChallengeItem = {
  id: number;
  name?: string | null;
  descr?: string | null;
  participants?: number | null;
  joined?: boolean | number | null;
  is_competition?: boolean | number | null;
  is_official?: boolean | number | null;
  life_cat?: string | null;
  difficulty?: string | null;
};

/** Stav všetkých filtrov obrazovky naraz. */
export type ChallengeFilters = {
  q: string;       // hľadaný text (názov + popis)
  join: string;    // all | in (zapojené) | out (nezapojené)
  type: string;    // all | chal (výzvy) | comp (súťaže)
  origin: string;  // all | official (oficiálne) | community (od ľudí)
  cat: string;     // all | kľúč životnej kategórie
  diff: string;    // all | easy | normal | hard
};

export type ChallengeFacet = keyof ChallengeFilters;
export type ChallengeSort = "popular" | "new";

export const CHAL_FILTER_DEFAULTS: ChallengeFilters = {
  q: "", join: "all", type: "all", origin: "all", cat: "all", diff: "all",
};

/** Bezdiakritické porovnanie — „sport" nájde „Šport", „behat" nájde „Behať". */
export const foldText = (s: unknown): string =>
  (s == null ? "" : String(s)).toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

/**
 * Náročnosť výzvy, alebo null keď nie je nastavená. Zámerne NEpoužíva `normDifficulty`
 * na prázdnu hodnotu — tá by „nenastavenú" ticho prehlásila za „normálnu" a výzvy bez
 * náročnosti (typicky súťaže) by spadli do nesprávnej pilulky.
 */
export const chalDifficulty = (c: ChallengeItem): Difficulty | null =>
  (c.difficulty ? normDifficulty(c.difficulty) : null);

/** Jedno miesto, kde je definovaný KAŽDÝ filter (kľúč → predikát). */
export const CHAL_MATCH: Record<ChallengeFacet, (c: ChallengeItem, v: string) => boolean> = {
  q: (c, v) => {
    const n = foldText(v).trim();
    return !n || foldText(c.name).includes(n) || foldText(c.descr).includes(n);
  },
  join: (c, v) => v === "all" || (v === "in" ? !!c.joined : !c.joined),
  type: (c, v) => v === "all" || (v === "comp" ? !!c.is_competition : !c.is_competition),
  origin: (c, v) => v === "all" || (v === "official" ? !!c.is_official : !c.is_official),
  cat: (c, v) => v === "all" || c.life_cat === v,
  diff: (c, v) => v === "all" || chalDifficulty(c) === v,
};

export const CHAL_FACETS = Object.keys(CHAL_MATCH) as ChallengeFacet[];

/** Vyhovuje všetkým filtrom okrem `skip` (skip = null ⇒ všetkým). */
export function matchesExcept(c: ChallengeItem, f: ChallengeFilters, skip: ChallengeFacet | null): boolean {
  return CHAL_FACETS.every((k) => k === skip || CHAL_MATCH[k](c, f[k]));
}

/** Výzvy, ktoré prejdú celým filtrom (bez radenia). */
export function filterChallenges(all: ChallengeItem[], f: ChallengeFilters): ChallengeItem[] {
  return all.filter((c) => matchesExcept(c, f, null));
}

/** Populárne = najviac účastníkov (pri zhode novšie prvé) · Najnovšie = najvyššie id. */
export function sortChallenges(list: ChallengeItem[], sort: ChallengeSort): ChallengeItem[] {
  return [...list].sort(sort === "new"
    ? (a, b) => b.id - a.id
    : (a, b) => ((b.participants || 0) - (a.participants || 0)) || (b.id - a.id));
}

/** Koľko výziev ostane, keď v danej fasete prepnem na `value` a ostatné filtre nechám. */
export function facetCount(all: ChallengeItem[], f: ChallengeFilters, facet: ChallengeFacet, value: string): number {
  return all.filter((c) => matchesExcept(c, f, facet) && CHAL_MATCH[facet](c, value)).length;
}

/** Existuje vôbec taká výzva (bez ohľadu na ostatné filtre)? Podľa toho sa pilulka ponúkne. */
export function facetExists(all: ChallengeItem[], facet: ChallengeFacet, value: string): boolean {
  return all.some((c) => CHAL_MATCH[facet](c, value));
}

/** Zužuje užívateľ zoznam (čokoľvek vrátane hľadania)? */
export const isNarrowed = (f: ChallengeFilters): boolean =>
  CHAL_FACETS.some((k) => f[k] !== CHAL_FILTER_DEFAULTS[k]);

/** Je zapnutý nejaký filter MIMO hľadania (podľa toho sa zvýrazní lievik)? */
export const hasFilter = (f: ChallengeFilters): boolean =>
  CHAL_FACETS.some((k) => k !== "q" && f[k] !== CHAL_FILTER_DEFAULTS[k]);
